import { usersAPI } from "../services/api";

/**
 * Turning on "tell me when a pre-order comes in" for this device.
 *
 * Same rule as the customer app: the browser's permission prompt is only ever
 * raised from a button the owner pressed, after being told what it is for. A
 * prompt that arrives cold gets refused, and a refusal is close to permanent.
 */

const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

export const pushSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/**
 * Why we cannot ask, when we cannot. iOS only lets a website send
 * notifications once it is on the home screen.
 */
export const pushBlocker = () => {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (isIos() && !isStandalone()) return "ios-needs-install";
  return null;
};

/**
 * The service worker that receives the notification. In production it is the
 * one that already caches the app shell. The development server never
 * registers it — a cached shell would serve yesterday's code on every reload —
 * so here it is registered with ?dev, which switches caching off and keeps
 * only the push handlers.
 */
const WORKER_URL =
  process.env.NODE_ENV === "production"
    ? "/service-worker.js"
    : "/service-worker.js?dev=1";

const registerWorker = async () => {
  try {
    return await navigator.serviceWorker.register(WORKER_URL);
  } catch {
    return null;
  }
};

/**
 * Whether alerts can be offered here, and whether this browser already has
 * them. Asked of the browser rather than the account: alerts on the owner's
 * phone say nothing about the tablet they are holding now.
 */
export const pushState = async () => {
  const blocker = pushBlocker();

  if (blocker === "unsupported") return { available: false, reason: blocker };

  try {
    const { data } = await usersAPI.pushStatus();

    if (!data.enabled || !data.public_key) {
      return { available: false, reason: "unconfigured" };
    }

    let subscribed = false;

    if (!blocker && Notification.permission === "granted") {
      const registration = await navigator.serviceWorker.getRegistration();
      subscribed = Boolean(await registration?.pushManager.getSubscription());
    }

    return { available: true, subscribed, reason: blocker };
  } catch {
    return { available: false, reason: "failed" };
  }
};

/**
 * Keeps a device that already said yes in step with the server, silently.
 *
 * Permission granted but the server no longer knows this browser — the database
 * was restored, the subscription was marked dead, the browser was signed into
 * another account — would leave reminders going nowhere while the screen
 * believed all was well. Re-registering is an upsert and raises no prompt (the
 * permission is already granted), so it is done whenever it is safe to, and
 * never when it would mean asking. Returns whether this device is now set up.
 */
export const syncPush = async () => {
  if (pushBlocker() || Notification.permission !== "granted") return { ok: false };

  try {
    const { data } = await usersAPI.pushStatus();
    if (!data.enabled || !data.public_key) return { ok: false };

    const registration = await registerWorker();
    if (!registration) return { ok: false };

    await navigator.serviceWorker.ready;

    const subscription =
      (await registration.pushManager.getSubscription()) ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      }));

    await usersAPI.subscribePush(subscription.toJSON());
    return { ok: true };
  } catch {
    return { ok: false };
  }
};

/** Asks, then subscribes. Returns what happened so the caller can say so. */
export const enablePush = async () => {
  const blocker = pushBlocker();
  if (blocker) return { ok: false, reason: blocker };

  const { data } = await usersAPI.pushStatus();
  if (!data.enabled || !data.public_key) {
    return { ok: false, reason: "unconfigured" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: permission };

  const registration = await registerWorker();
  if (!registration) return { ok: false, reason: "no-worker" };

  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();

  const subscription =
    existing ||
    (await registration.pushManager.subscribe({
      // Required by every browser: a push that shows nothing is not allowed
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    }));

  await usersAPI.subscribePush(subscription.toJSON());

  return { ok: true };
};
